import {
    Component,
    OnInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Input,
    ViewChild,
    SimpleChanges,
    Output,
    EventEmitter,
    OnChanges,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
    LocationAttachment,
    LocationsApiService,
    PartialCeLocation,
    PatchLocationRequest,
} from '@kempoweroyj/ngx-locations-api-client';
import { Observable, fromEvent } from 'rxjs';
import {
    isAvailable,
    isFaulted,
    isFinishing,
    isOccupied,
    isPreparing,
    isSelfTest,
} from 'shared/connector-status-pipes/connector-status.pipe';
import { KpMapComponent } from 'shared/kp-map/components/kp-map/kp-map.component';
import { FeatureStatus, Feature, FeatureCollection, ImageInFeature, ImageOnMap } from 'shared/kp-map/models';
import { TileProvider } from 'shared/kp-map/services/tile.service';
import { PopupService } from 'shared/popup-service/popup.service';
import { CeLocationRow } from 'shared/stations-service/locations.service';
import { Satellite, StationsService } from 'shared/stations-service/stations.service';
import { StationStatusInfo } from '../station-status-info';
import * as L from 'leaflet';
import { cloneDeep, isEmpty } from 'lodash';
import { FileItem, FileUploader } from 'ng2-file-upload';
import { FormatBytesPipe } from 'shared/pipes/format-bytes-pipe/format-bytes.pipe';
import { DialogLinkService } from 'shared/routed-dialog/dialog-link.service';
import { LocationWithPowerUsageIncluded } from 'fleet/map-page/fleet-map-page.component';
import { ConfigService } from '../../../../providers/config.service';
import { EvseApiService } from '@kempoweroyj/ngx-evse-api-client';

export type LocationAttachmentFileItem = FileItem & { attachment: LocationAttachment };
@Component({
    selector: 'ce-map',
    templateUrl: './map.component.html',
    styleUrls: ['./map.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
@UntilDestroy()
export class CeMapComponent implements OnInit, OnChanges {
    @Input() stations: StationStatusInfo[] = [];
    @Input() location: CeLocationRow;
    @Input() maximized: boolean;
    @Input() locationsPowerMap: LocationWithPowerUsageIncluded[];
    @Input() mapUpdateEvent$: Observable<void>;
    @Input() loadingMapData$: Observable<boolean>;

    @Output() locationChanged: EventEmitter<PartialCeLocation> = new EventEmitter();

    locationId: string;
    defaultLocation: L.LatLngExpression = [50.27836640515094, 8.115810993194996];

    // kp-map
    // !!Do not modify this.location directly. Modify mapData instead.
    // !! this.location is used as source of truth, and checking point to revert (e.g using cancel() to dismiss unsaved changes)
    isEditable = true;
    isEditing = false;
    isSaving = false;

    hasLocation = false;

    tileProvider: TileProvider = 'mapTilerStreet';
    mapTilerToken: string;
    mapData: FeatureCollection;
    zoomLevel: number;

    // uploader
    uploader = new FileUploader({
        url: '',
        method: 'PUT',
        disableMultipart: true,
        queueLimit: 1,
        maxFileSize: 1 * 1024 * 1024 * 1024,
    });

    @ViewChild('map', { read: KpMapComponent, static: true }) map: KpMapComponent;

    constructor(
        private cdRef: ChangeDetectorRef,
        private stationsService: StationsService,
        private locationsApi: LocationsApiService,
        private evseApi: EvseApiService,
        private dialogLink: DialogLinkService,
        private popup: PopupService,
        private formatBytesPipe: FormatBytesPipe,
        private configService: ConfigService
    ) {
        this.mapTilerToken = this.configService.config.mapTilerApiKey;
        if (!this.mapTilerToken) {
            // Fail loudly rather than silently falling back to a shared key: every deployed
            // config.*.json must define mapTilerApiKey. See KC-11189.
            // The map still renders — kp-map degrades to OpenStreetMap tiles — but the MapTiler
            // layers and the "Jump to address" geocoding control are lost.
            console.error(
                'mapTilerApiKey is not set in the environment config - falling back to OpenStreetMap tiles, address search disabled.'
            );
        }
    }

    async ngOnInit() {
        this.mapUpdateEvent$?.pipe(untilDestroyed(this)).subscribe(async () => {
            await this.updateMapData();
        });

        // Immediately load from this.location.geo, do not wait for fetching background images
        if (this.location?.geo) {
            this.mapMutated({
                ...this.mapData,
                features: [...this.location.geo],
            });
        }

        await this.updateMapData();

        // uploader
        this.uploader.onBeforeUploadItem = async (item) => {
            // Uploader uploads items 1 by 1 so always make sure the content-type is set correctly
            this.uploader.options.headers = [{ name: 'Content-Type', value: item._file.type }];
            // set withCredentials to false to avoid cors problems
            item.withCredentials = false;
            if (item._file.size >= this.uploader.options.maxFileSize) {
                this.popup.error(
                    `Error uploading file ${item._file.name}: File too big. Max file size (${this.formatBytesPipe.transform(this.uploader.options.maxFileSize)}) exceeded`
                );
                item.remove();
                // Error, revert map to last saved stage
                await this.updateMapData();
            }
        };

        this.uploader.onWhenAddingFileFailed = async (item) => {
            console.error('map-attachment-upload#onWhenAddingFileFailed', { item });
            if (this.uploader.queue.length === this.uploader.options.queueLimit) {
                this.popup.error(
                    `Error uploading file ${item.name}: Upload queue limit (${this.uploader.options.queueLimit}) exceeded`
                );
            } else if (item.size >= this.uploader.options.maxFileSize) {
                this.popup.error(
                    `Error uploading file ${item.name}: File too big. Max file size (${this.formatBytesPipe.transform(this.uploader.options.maxFileSize)}) exceeded`
                );
            }

            // Error, revert map to last saved stage
            await this.updateMapData();
        };

        this.uploader.onProgressItem = () => {
            this.cdRef.markForCheck();
        };

        this.uploader.onErrorItem = async (item, response) => {
            this.popup.error('Error while uploading file.');
            console.error(response);
            this.uploader.removeFromQueue(item);

            // Error, revert map to last saved stage
            await this.updateMapData();
            this.cdRef.markForCheck();
        };

        this.uploader.onSuccessItem = async (item: LocationAttachmentFileItem) => {
            this.popup.success(`Picture uploaded`, { durationMs: 2000 });

            this.uploader.removeFromQueue(item);

            // Upload finished, fetch download url from s3 and start mutating map
            const downloaded = await this.locationsApi.getLocationAttachments(
                this.location.id,
                item.attachment.fileKey
            );
            const locationFeature = this.mapData.features.find((f) => f.properties.type === 'location');
            if (locationFeature && !isEmpty(downloaded.attachments)) {
                const mutatedLocationFeature = cloneDeep(locationFeature);

                mutatedLocationFeature.properties.backgroundImageFileKey = item.attachment.fileKey;
                mutatedLocationFeature.properties.url = downloaded.attachments[0].temporaryUrl;
                mutatedLocationFeature.properties.backgroundImageFileName = item.attachment.fileName;

                const featuresWithImages = this.mapData.features.map((f) => {
                    if (f.properties.type === 'location') {
                        return mutatedLocationFeature;
                    } else {
                        return f;
                    }
                });

                const mutatedMapData: FeatureCollection = {
                    ...cloneDeep(this.mapData),
                    features: [...featuresWithImages],
                };

                this.mapMutated(mutatedMapData);
            }

            this.cdRef.markForCheck();
        };
    }

    isZoomedInEnoughForPlaceLocation = false;
    ngAfterViewInit() {
        fromEvent(this.map.map, 'zoom')
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.zoomLevel = this.map.map.getZoom();

                const mapBounds = this.map.map.getBounds();
                const mapBoundsDistance = mapBounds.getNorthEast().distanceTo(mapBounds.getSouthWest());
                const THRESHOLD_METERS = 300;
                this.isZoomedInEnoughForPlaceLocation = mapBoundsDistance < THRESHOLD_METERS;
                this.cdRef.markForCheck();
            });

        this.zoomLevel = this.map.map.getZoom();
        this.cdRef.markForCheck();

        this.map.map.invalidateSize();
        this.map.resetZoom();

        setTimeout(() => {
            this.map.map.invalidateSize();
            this.map.resetZoom();
        }, 1000);
        setTimeout(() => {
            this.map.map.invalidateSize();
        }, 5000);
    }

    async ngOnChanges(changes: SimpleChanges) {
        if (changes.maximized) {
            setTimeout(() => {
                this.map.map.invalidateSize();
            });
        }

        const { location, stations } = changes;

        if (location || stations) {
            await this.updateMapData();
            this.cdRef.markForCheck();
        }
    }

    async ngOnDestroy() {
        // find unused attachments
        const used: string[] = [];
        this.mapData.features.forEach((f) => {
            if (f.properties.url) {
                used.push(f.properties.backgroundImageFileKey);
            }
        });
        // delete unused attachments
        await this.removeUnusedImages(used);
    }

    async updateMapData() {
        if (this.location) {
            this.locationId = this.location.id;
        }
        // or revert unsaved changes
        this.mapData = await this.generateGeoJson();
        this.updateHasLocation();
        this.cdRef.markForCheck();
    }

    updateHasLocation() {
        this.hasLocation = this.mapData?.features?.some(
            (f) => f?.properties?.type === 'location' && f?.properties?.located
        );
        this.cdRef.markForCheck();
    }

    mapMutated(data: FeatureCollection) {
        this.mapData = data;
        this.updateHasLocation();
        this.cdRef.markForCheck();
    }

    featureClicked(feature: Feature) {
        if (feature.properties.type === 'connector') {
            this.dialogLink.open(['connector', feature.properties.stationId, feature.properties.evseId]);
        } else if (feature.properties.type === 'satellite' || feature.properties.type === 'trolley') {
            const satelliteId = feature.properties.id.split('#')[1];
            this.dialogLink.open(['satellite', feature.properties.stationId, satelliteId]);
        }
    }

    setAsLocation() {
        const mapBounds = this.map.map.getBounds().pad(-0.2);
        const northWest = mapBounds.getNorthWest();
        const southEast = mapBounds.getSouthEast();

        const locationBounds: [[number, number], [number, number]] = [
            [northWest.lng, northWest.lat],
            [southEast.lng, southEast.lat],
        ];
        const defaultMapData: FeatureCollection = {
            ...this.mapData,
            features: [
                this.generateLocationFeature(locationBounds), // Dismiss current location feature's placement
                ...this.placeSatelliteDefaults(locationBounds), // Dismiss all current satellite features' placements
            ],
        };

        this.mapMutated(defaultMapData);

        this.map.toggleEditing(true);
    }

    placeSatelliteDefaults(locationBounds: [[number, number], [number, number]]): Feature[] {
        const satelliteDefaults: Feature[] = [];

        let satelliteIdx = 0;
        for (const station of this.stations) {
            const satellites = this.stationsService.getSatellites(station.station, station.connectors);

            for (const satellite of satellites) {
                const activeConnectors = satellite.connectors.filter((c) => c.active);
                if (!activeConnectors.length) {
                    // satellite has no active connectors, skip
                    continue;
                }

                satelliteIdx++;

                // place satellite
                const satelliteCoordinates: [number, number] = [
                    locationBounds[0][0] + 0.00005,
                    locationBounds[0][1] - 0.00005 * satelliteIdx,
                ];

                const satelliteFeature = this.generateSatelliteFeature(
                    station.station.station.stationId,
                    satellite,
                    satelliteCoordinates,
                    station.station.station.name
                );
                satelliteDefaults.push(satelliteFeature);

                // place connectors
                let connectorIdx = 0;
                for (const connector of activeConnectors) {
                    const _connector = cloneDeep(connector);
                    _connector.lng = satelliteCoordinates[0] + 0.000023;
                    _connector.lat = satelliteCoordinates[1] + 0.000007 - 0.000015 * connectorIdx;

                    satelliteDefaults.push(this.generateConnectorFeature(satelliteFeature, _connector));

                    connectorIdx++;
                }
            }
        }

        return satelliteDefaults;
    }

    getDefaultCoordinates(
        linkedTo: [number, number] = undefined,
        evseId: string = undefined,
        satelliteId: string = undefined
    ): [number, number] {
        // Setting base coordinates -- [lng, lat]
        let _base = [this.defaultLocation[0], this.defaultLocation[1]] as [number, number];

        const mapBounds = this.map?.map?.getBounds()?.pad(-0.2);
        if (mapBounds) {
            const northWest = mapBounds.getNorthWest();
            _base = [northWest.lng, northWest.lat];
        }
        if (this.location.bounds) {
            _base = [this.location.bounds[0][0], this.location.bounds[0][1]];
        }
        if (linkedTo) {
            _base = linkedTo;
        }

        // Randomize to prevent overlapping
        let _lng = _base[0]; // based on connector id to set right or left. If not connector, set as satellites
        if (evseId) {
            if (Number(evseId.slice(-1)) === 2) {
                _lng = _base[0] + 0.00003; // right
            } else if (Number(evseId.slice(-1)) === 1) {
                _lng = _base[0] - 0.00003; // left
            }
        } else if (satelliteId) {
            _lng = _base[0] + Number('0.000'.concat(satelliteId)); // satellites
        }

        const _lat = _base[1] + 0.00002; // higher than base a bit

        // Return
        return [_lng, _lat]; // make sure to match geoJSON order
    }

    async generateGeoJson() {
        const mapData: FeatureCollection = {
            type: 'FeatureCollection',
            features: [],
        };

        if (!this.location) {
            return mapData;
        }

        // location
        const locationFeature = this.getLocationGeoFeature();
        if (locationFeature) {
            if (locationFeature.properties.url) {
                // get updated background image url
                const downloaded = await this.locationsApi.getLocationAttachments(
                    this.location.id,
                    locationFeature.properties.backgroundImageFileKey
                );
                if (!isEmpty(downloaded.attachments)) {
                    locationFeature.properties.url = downloaded.attachments[0].temporaryUrl;
                }
            }

            // utilize saved location
            mapData.features.push(locationFeature);
        } else {
            if (this.location.bounds) {
                const coords = [
                    [this.location.bounds[0][0][0], this.location.bounds[0][0][1]],
                    [this.location.bounds[0][1][0], this.location.bounds[0][1][1]],
                ] as [[number, number], [number, number]];
                mapData.features.push(this.generateLocationFeature(coords));
            }
        }

        // bail out here if location not yet placed
        if (!this.location.bounds) {
            return mapData;
        }

        // generate satellites and connectors
        for (const station of this.stations) {
            const stationId = station.station.station.stationId;
            const stationName = station.station.station.name;
            const satellites = this.stationsService.getSatellites(station.station, station.connectors);
            for (const satellite of satellites) {
                const activeConnectors = satellite.connectors.filter((c) => c.active);
                if (!activeConnectors.length) {
                    // satellite has no active connectors, skip
                    continue;
                }

                // include satellite in map, find coordinates in location's geo object
                const satelliteCoordinates = this.getSatelliteGeoFeature(
                    station.station.station.stationId,
                    satellite.satelliteId
                )?.geometry?.coordinates as [number, number];
                const satelliteFeature = this.generateSatelliteFeature(
                    stationId,
                    satellite,
                    satelliteCoordinates,
                    stationName
                );
                mapData.features.push(satelliteFeature);

                // include connectors
                for (const connector of activeConnectors) {
                    const connectorFeature = this.generateConnectorFeature(satelliteFeature, connector);
                    mapData.features.push(connectorFeature);
                }
            }
        }

        return mapData;
    }

    getSatelliteGeoFeature(stationId: string, satelliteId: number) {
        const locationGeo: Feature[] = cloneDeep(this.location.geo) || []; // prevent referencing and modifying upstream
        const hasNoFeatures = locationGeo?.every((value) => value === null);

        return !hasNoFeatures
            ? locationGeo?.find(
                  (f) =>
                      (f.properties?.type === 'satellite' || f.properties?.type === 'trolley') &&
                      f.properties?.id === `${stationId}#${satelliteId}`
              )
            : null;
    }

    getLocationGeoFeature() {
        const locationGeo: Feature[] = cloneDeep(this.location.geo) || []; // prevent referencing and modifying upstream
        const hasNoFeatures = locationGeo?.every((value) => value === null);

        return !hasNoFeatures ? locationGeo.find((f) => f.properties?.type === 'location') : null;
    }

    generateConnectorFeature(satelliteFeature: Feature, connector: Satellite['connectors'][0]): Feature {
        // The order in GeoJSON is longitude, latitude.
        // GeoJSON:
        //   {"type": "Point", "coordinates": [lon, lat]}
        // https://datatracker.ietf.org/doc/html/rfc7946#section-9
        const connectorCoordinates: [number, number] =
            connector.lat && connector.lng ? [connector.lng, connector.lat] : undefined;
        const connectorFeature: Feature = {
            type: 'Feature',
            properties: {
                located: true,
                id: `${satelliteFeature.properties.id}#${connector.evseId}`,
                name: connector.evseName || connector.evseId,
                type: 'connector',
                parent: satelliteFeature.properties.id,
                status: this.mapConnectorStatus(connector),
                stationId: connector.stationId,
                evseId: connector.evseId,
            },
            geometry: {
                type: 'Point',
                coordinates:
                    connectorCoordinates ||
                    this.getDefaultCoordinates(
                        satelliteFeature.geometry.coordinates as [number, number],
                        connector.evseId
                    ),
            },
        };

        return connectorFeature;
    }

    generateSatelliteFeature(
        stationId: string,
        satellite: Satellite,
        coordinates: [number, number],
        stationName: string = undefined
    ): Feature {
        const satelliteFeatureId = `${stationId}#${satellite.satelliteId}`;
        const isTrolley = satellite.type === 'TROLLEY';

        // include satellite in map
        const satelliteFeature: Feature = {
            type: 'Feature',
            properties: {
                id: satelliteFeatureId,
                located: true,
                name:
                    (isTrolley ? `Trolley` : `Satellite ${satellite.satelliteId}`) +
                    ' ' +
                    `${stationName || stationId}`,
                type: isTrolley ? 'trolley' : 'satellite',
                parent: this.location.id,
                stationId: satellite.station.station.stationId,
            },
            geometry: {
                type: 'Point',
                coordinates:
                    coordinates || this.getDefaultCoordinates(undefined, undefined, String(satellite.satelliteId)),
            },
        };

        return satelliteFeature;
    }

    generateLocationFeature(bounds: [[number, number], [number, number]] = undefined) {
        // defaults
        const locationDefaultFallbackCoordinates: [[number, number], [number, number]] = [
            this.getDefaultCoordinates(),
            this.getDefaultCoordinates(),
        ];
        const locationFeature: Feature = {
            type: 'Feature',
            properties: {
                id: this.location.id,
                type: 'location',
                located: bounds ? true : false,
                name: this.location.name,
            },
            geometry: {
                type: 'LineString',
                coordinates: bounds || locationDefaultFallbackCoordinates,
            },
        };

        return locationFeature;
    }

    mapConnectorStatus(connector: Satellite['connectors'][0]): FeatureStatus {
        if (isAvailable(connector)) {
            return 'empty'; // green (available) color
        } else if (isOccupied(connector)) {
            if (isPreparing(connector) || isFinishing(connector)) {
                return 'reserved'; // black/dark gray (preparing/finishing) color
            } else {
                return 'charging'; // blue (charging) color
            }
        } else if (isFaulted(connector)) {
            return 'error'; // red (faulted) color
        } else if (isSelfTest(connector)) {
            return 'self-test'; // yellow (self-test) color
        } else {
            return 'disabled'; // gray (unavailable/unknown) color
        }
    }

    // TODO: Reset location, should allow user to select new location's coors (using setAsLocation and setSatelliteDefaults)
    removeFeature(_id: string): void {
        // todo implementation
    }

    async save(data: FeatureCollection) {
        // this save function will disable editing (via kp-map), ngOnChanges will call updateMapData(), making features on map move unexpectedly
        this.isSaving = true;

        this.cdRef.markForCheck();

        try {
            this.popup.neutral(`Saving...`);

            // prepare location
            const location = data.features.find((f) => f.properties.type === 'location');
            // Leaflet library, L, is using lat, lng order -- location.geometry.coordinates is using geoJSON order lng, lat
            const geoJsonBounds = location.geometry.coordinates as [[number, number], [number, number]];
            // Swap the long,lat order to lat,long order for Leaflet
            const locationBounds = L.latLngBounds(
                [geoJsonBounds[0][1], geoJsonBounds[0][0]],
                [geoJsonBounds[1][1], geoJsonBounds[1][0]]
            );
            const locationCenter = locationBounds.getCenter();

            // removing background image if location does not have url
            if (!location.properties.url) {
                delete location.properties.url;
                delete location.properties.backgroundImageFileKey;
                delete location.properties.backgroundImageFileName;
            }

            // prepare final object
            const modified: PatchLocationRequest = {
                location: {
                    lat: locationCenter.lat,
                    lng: locationCenter.lng,
                    bounds: location.geometry.coordinates as [[number, number], [number, number]],
                    geo: [location],
                },
            };

            // checking if the satellite is within the location bounds
            const satellites = data.features.filter(
                (f) => f.properties.type === 'satellite' || f.properties.type === 'trolley'
            );

            let satelliteIdx = 0;
            const satellitesSeparator = 0.00005;
            const connectorsSeparator = 0.00003;
            const connectorPromiseArr: Promise<any>[] = [];

            satellites.forEach((sat) => {
                const satelliteInsideBounds = withinBoundsChecker(locationBounds, sat);

                const connectors = data.features.filter(
                    (f) => f.properties.type === 'connector' && f.properties.parent === sat.properties.id
                );

                if (satelliteInsideBounds) {
                    // If the satellite coordinates are within the bounds of the location, push satellite coordinates as is
                    modified.location.geo.push(sat);

                    this._placeConnectors(locationBounds, sat, connectors, connectorsSeparator, connectorPromiseArr); // check and place connectors
                } else {
                    // Else, apply default satellite coordinates
                    satelliteIdx++;
                    const modifiedLocationEastBound = modified.location.bounds[1][0];
                    // coordinates in GeoJSON order: longitude, latitude
                    sat.geometry.coordinates = [
                        modifiedLocationEastBound - satellitesSeparator * satelliteIdx,
                        modified.location.lat,
                    ];
                    modified.location.geo.push(sat); // push modified satellite coordinates

                    this._placeConnectors(locationBounds, sat, connectors, connectorsSeparator, connectorPromiseArr); // check and place connectors
                }
            });

            await Promise.all(connectorPromiseArr);
            await this.locationsApi.patchLocation(location.properties.id, modified);

            this.isSaving = false;
            this.cdRef.markForCheck();

            this.popup.success(`Saved.`);
            this.locationChanged.emit(modified.location); // bubble changes to refresh map
        } catch (e) {
            this.isSaving = false;
            this.cdRef.markForCheck();

            console.error(e);
            this.popup.error(`Could not save.`);
        }
    }

    /**
     * Check for each connector if it is within the location bounds, either apply coordinates as is/revert to default, then push data to promise array for saving
     *
     * @param locationBounds the bounds in lat, long (N, E) order
     * @param satellite the satellite to check, with geometry.coordinates in the geoJSON order (long = E, lat = N)
     * @param connectors the connectors to check, with geometry.coordinates in the geoJSON order (long = E, lat = N)
     * @param connectorsSeparator the distance between connectors
     * @param promiseArr the array of promises to push data to
     */
    async _placeConnectors(
        locationBounds: L.LatLngBounds,
        satellite: Feature,
        connectors: Feature[],
        connectorsSeparator: number,
        promiseArr: Promise<any>[]
    ) {
        for (const connector of connectors) {
            // connectorCoordinates is in GeoJSON order: longitude, latitude
            let connectorCoordinates: [number, number];
            const connectorInsideBounds = withinBoundsChecker(locationBounds, connector);

            // If Connector is within the location bounds
            if (connectorInsideBounds) {
                connectorCoordinates = connector.geometry.coordinates as [number, number];
            } else {
                if (Number(connector.properties.evseId.slice(-1)) === 2) {
                    connectorCoordinates = [
                        Number(satellite.geometry.coordinates[0]) + connectorsSeparator,
                        Number(satellite.geometry.coordinates[1]),
                    ]; // right
                } else if (Number(connector.properties.evseId.slice(-1)) === 1) {
                    connectorCoordinates = [
                        Number(satellite.geometry.coordinates[0]) - connectorsSeparator,
                        Number(satellite.geometry.coordinates[1]),
                    ]; // left
                } else {
                    connectorCoordinates = [
                        Number(satellite.geometry.coordinates[0]) + connectorsSeparator * 2,
                        Number(satellite.geometry.coordinates[1]),
                    ]; // more than two connectors
                }
            }

            promiseArr.push(
                this.evseApi.putEvse(connector.properties.stationId, connector.properties.evseId, {
                    lat: connectorCoordinates[1],
                    lng: connectorCoordinates[0],
                })
            );
        }
    }

    async cancel() {
        await this.updateMapData();
    }

    editStart() {
        this.isEditing = true;
        this.cdRef.markForCheck();
    }

    editEnd() {
        this.isEditing = false;
        this.cdRef.markForCheck();
    }

    uploadImg(_imageOnMap: ImageOnMap) {
        // todo: implementation
    }

    async setImg(imageInFeature: ImageInFeature) {
        // Prepare attachment
        const attachment = {
            fileName: imageInFeature.file.name,
            contentType: imageInFeature.file.type,
        };

        // Change normal file to FileItem by adding to queue
        this.uploader.addToQueue([imageInFeature.file]);

        // Get upload url
        const returnedAttachment = await this.locationsApi.postSingleLocationAttachment(imageInFeature.id, {
            attachment,
        });

        // Upload attachment and get back upload url
        this.upload(
            imageInFeature.file,
            returnedAttachment.attachment.temporaryUploadUrl,
            returnedAttachment.attachment
        );
    }

    private async upload(file: File, url: string, attachment: LocationAttachment) {
        this.uploader.options.headers = [{ name: 'Content-Type', value: file.type }];

        try {
            // Get FileItem of the original file
            const fileItem = this.uploader.queue.find((e) => e._file.name === file.name);

            // Add upload url and actual attachment
            fileItem.url = url;
            (fileItem as LocationAttachmentFileItem).attachment = attachment;

            // Uploading
            if (!fileItem.isUploading) {
                this.uploader.uploadItem(fileItem);

                // Handling uploaded in the this.uploader.onSuccessItem
            }
        } catch (error) {
            // see also uploader errorhandler defined in #ngOnInit()
            console.error(error, { file, url, attachment });
        }
    }

    private async removeUnusedImages(usedFileKeys: string[]) {
        const removeAttachmentPromises = [];

        if (this.location) {
            const res = await this.locationsApi.getLocationAttachments_(this.location.id);

            if (!isEmpty(res.attachments)) {
                const allFileKeys = res.attachments.map((a) => a.fileKey);
                allFileKeys.forEach((key) => {
                    if (!usedFileKeys.includes(key)) {
                        removeAttachmentPromises.push(
                            this.locationsApi.deleteSingleLocationAttachment(this.location.id, key)
                        );
                    }
                });
            }

            await Promise.all(removeAttachmentPromises);
        }
    }
}

/**
 * Check if an element is within bounds
 *
 * @param latLngBounds the bounds in lat, long (N, E) order
 * @param checkedDevice the device to check, with geometry.coordinates in the geoJSON order (long = E, lat = N)
 * @returns true if the device is within the bounds, false otherwise
 */
function withinBoundsChecker(latLngBounds: L.LatLngBounds, checkedDevice: Feature) {
    // compare Location bounds with device coordinates
    const north = latLngBounds.getNorth() > Number(checkedDevice.geometry.coordinates[1]);
    const east = latLngBounds.getEast() > Number(checkedDevice.geometry.coordinates[0]);
    const south = latLngBounds.getSouth() < Number(checkedDevice.geometry.coordinates[1]);
    const west = latLngBounds.getWest() < Number(checkedDevice.geometry.coordinates[0]);
    const insideBounds = north && east && south && west;

    return insideBounds;
}
